import { getReportingPeriodIso, type ReportingPeriodCustom } from './reportingPeriod';

const PLACEMENT_SELECT =
    'id, candidate_id, vacancy_id, client_id, opdrachtgever_id, eindklant_id, number, status, source, created_at, updated_at, start_date, end_date, data, candidate:candidates(id,name), vacancy:vacancies(id,title,opdrachtgever_id,eindklant_id)';

export function placementInReportingPeriod(
    p: { created_at?: string | null; start_date?: string | null },
    filterPeriod: string,
    custom?: ReportingPeriodCustom
): boolean {
    if (filterPeriod === 'all') return true;
    const { dateFrom, dateTo } = getReportingPeriodIso(filterPeriod, custom);
    const from = new Date(dateFrom).getTime();
    const to = new Date(dateTo).getTime();
    if (!Number.isFinite(from) || !Number.isFinite(to)) return true;

    const created = p.created_at ? new Date(p.created_at).getTime() : NaN;
    if (Number.isFinite(created) && created >= from && created <= to) return true;

    if (p.start_date) {
        const start = new Date(p.start_date).getTime();
        if (Number.isFinite(start) && start >= from && start <= to) return true;
    }
    return false;
}

async function fetchPlacementPages(
    runQuery: (from: number, to: number) => Promise<{ data: any[] | null; error: any }>
): Promise<any[]> {
    const pageSize = 1000;
    const rows: any[] = [];
    let offset = 0;
    for (;;) {
        const { data, error } = await runQuery(offset, offset + pageSize - 1);
        if (error) throw error;
        if (!data?.length) break;
        rows.push(...data);
        if (data.length < pageSize) break;
        offset += pageSize;
    }
    return rows;
}

export type FetchReportingScopedPlacementsOpts = {
    filterPeriod: string;
    periodCustom?: ReportingPeriodCustom;
    /** Intern: opdrachtgever-filter (filterClientId) */
    opdrachtgeverId?: string;
    eindklantId?: string;
    vacancyIds?: string[];
};

/**
 * Alle placement-rijen voor rapportage-scope (dedupe op id).
 * Bij opdrachtgever-filter: volledige tabel op `opdrachtgever_id`, niet beperkt tot vacature-lijst.
 */
export async function fetchReportingScopedPlacements(
    supabase: { from: (t: string) => any },
    opts: FetchReportingScopedPlacementsOpts
): Promise<any[]> {
    const byId = new Map<string, any>();
    const merge = (rows: any[]) => {
        for (const r of rows) {
            const id = String(r?.id || '').trim();
            if (id) byId.set(id, r);
        }
    };

    const og = String(opts.opdrachtgeverId || '').trim();
    const ek = String(opts.eindklantId || '').trim();
    const vacIds = (opts.vacancyIds || []).map((id) => String(id).trim()).filter(Boolean);

    if (og) {
        const rows = await fetchPlacementPages((from, to) =>
            supabase
                .from('placements')
                .select(PLACEMENT_SELECT)
                .eq('opdrachtgever_id', og)
                .order('created_at', { ascending: false })
                .range(from, to)
        );
        merge(rows.filter((p) => placementInReportingPeriod(p, opts.filterPeriod, opts.periodCustom)));
    }

    if (ek) {
        const rows = await fetchPlacementPages((from, to) =>
            supabase
                .from('placements')
                .select(PLACEMENT_SELECT)
                .eq('eindklant_id', ek)
                .order('created_at', { ascending: false })
                .range(from, to)
        );
        merge(rows.filter((p) => placementInReportingPeriod(p, opts.filterPeriod, opts.periodCustom)));
    }

    if (!og && !ek && vacIds.length) {
        for (let i = 0; i < vacIds.length; i += 80) {
            const chunk = vacIds.slice(i, i + 80);
            const rows = await fetchPlacementPages((from, to) =>
                supabase
                    .from('placements')
                    .select(PLACEMENT_SELECT)
                    .in('vacancy_id', chunk)
                    .order('created_at', { ascending: false })
                    .range(from, to)
            );
            merge(rows.filter((p) => placementInReportingPeriod(p, opts.filterPeriod, opts.periodCustom)));
        }
    }

    if ((og || ek) && vacIds.length) {
        for (let i = 0; i < vacIds.length; i += 80) {
            const chunk = vacIds.slice(i, i + 80);
            const rows = await fetchPlacementPages((from, to) =>
                supabase
                    .from('placements')
                    .select(PLACEMENT_SELECT)
                    .in('vacancy_id', chunk)
                    .order('created_at', { ascending: false })
                    .range(from, to)
            );
            merge(rows.filter((p) => placementInReportingPeriod(p, opts.filterPeriod, opts.periodCustom)));
        }
    }

    return Array.from(byId.values()).sort(
        (a, b) =>
            new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
    );
}
